// src/pages/staff/StaffPage.tsx

import React, { useState, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Input,
  Select,
  Row,
  Col,
  Avatar,
  Modal,
  Statistic,
  Badge,
  Dropdown,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  UserOutlined,
  EditOutlined,
  MoreOutlined,
  ReloadOutlined,
  FilterOutlined,
  ExportOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  StopOutlined,
  PlayCircleOutlined,
  SecurityScanOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useStaffManagement } from '../../hooks/useStaff';
import StaffCreateModal from './components/StaffCreateModal';
import StaffEditModal from './components/StaffEditModal';
import StaffDetailModal from './components/StaffDetailModal';
import { useAuth } from '../../hooks/useAuth';
import { useAuthContext } from '../../contexts/AuthContext';
import type { StaffMember, StaffSearchParams } from '../../types/api.types';
import { STAFF_ROLE_OPTIONS } from '../../types/api.types';
import StaffAdvancedFilters from './components/StaffAdvancedFilters';
import StaffExportModal from './components/StaffExportModal';
import CommunalAdminCreateModal from '../communes/components/CommunalAdminCreateModal';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const StaffPage: React.FC = () => {
  // État local
  const [searchParams, setSearchParams] = useState<StaffSearchParams>({
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(null);
  const [modalStates, setModalStates] = useState({
    create: false,
    edit: false,
    detail: false,
    filters: false,
    export: false,
    createCommunalAdmin: false,
  });

  // Hooks
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isSuperAdmin } = useAuthContext();
  const {
    staff,
    pagination,
    isLoading,
    createStaff,
    updateStaff,
    activateStaff,
    deactivateStaff,
    isCreating,
    isUpdating,
    refetch,
  } = useStaffManagement(searchParams);

  // Fonctions utilitaires
  const openModal = (type: keyof typeof modalStates, staffMember?: StaffMember) => {
    if (staffMember) setSelectedStaff(staffMember);
    setModalStates(prev => ({ ...prev, [type]: true }));
  };

  const closeModal = (type: keyof typeof modalStates) => {
    setModalStates(prev => ({ ...prev, [type]: false }));
    if (type !== 'create') setSelectedStaff(null);
  };

  const handleSearch = (value: string) => {
    setSearchParams(prev => ({ ...prev, search: value, page: 1 }));
  };

  const handleFilterChange = (key: keyof StaffSearchParams, value: any) => {
    setSearchParams(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleTableChange = (pagination: any, sorter: any) => {
    setSearchParams(prev => ({
      ...prev,
      page: pagination.current,
      limit: pagination.pageSize,
      sortBy: sorter.field || 'createdAt',
      sortOrder: sorter.order === 'ascend' ? 'asc' : 'desc',
    }));
  };

  const handleActivateStaff = (staffId: string) => {
    activateStaff(staffId);
  };

  const handleDeactivateStaff = (staffId: string) => {
    Modal.confirm({
      title: 'Confirmer la désactivation',
      content: 'Êtes-vous sûr de vouloir désactiver ce membre du personnel ?',
      okText: 'Oui, désactiver',
      cancelText: 'Annuler',
      okType: 'danger',
      onOk: () => {
        deactivateStaff({ id: staffId, reason: 'Désactivé par l\'administrateur' });
      },
    });
  };

  // Colonnes du tableau
  const columns: ColumnsType<StaffMember> = [
    {
      title: 'Personnel',
      key: 'staff',
      width: 280,
      render: (_, record) => (
        <Space size="middle">
          <Avatar
            size={48}
            icon={<UserOutlined />}
            style={{
              backgroundColor: record.isActive ? '#52c41a' : '#d9d9d9',
              color: 'white',
            }}
          >
            {`${record.User.firstName[0]}${record.User.lastName[0]}`}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600, fontSize: '16px' }}>
              {`${record.User.firstName} ${record.User.lastName}`}
            </div>
            <div style={{ color: '#666', fontSize: '14px' }}>
              {record.User.email}
            </div>
            <div style={{ color: '#999', fontSize: '12px' }}>
              {record.staffNumber}
            </div>
          </div>
        </Space>
      ),
      sorter: true,
    },
    {
      title: 'Rôle',
      dataIndex: 'role',
      key: 'role',
      width: 150,
      render: (role: string) => {
        const roleConfig = STAFF_ROLE_OPTIONS.find(r => r.value === role) || {
          label: role,
          color: 'default'
        };
        return (
          <Tag color={roleConfig.color} style={{ borderRadius: '6px' }}>
            {roleConfig.label}
          </Tag>
        );
      },
      filters: STAFF_ROLE_OPTIONS.map(role => ({
        text: role.label,
        value: role.value,
      })),
    },
    {
      title: 'Département',
      dataIndex: 'department',
      key: 'department',
      width: 150,
      render: (department: string | null) => department || '-',
    },
    {
      title: 'Position',
      dataIndex: 'position',
      key: 'position',
      width: 150,
      render: (position: string | null) => position || '-',
    },
    {
      title: 'Date d\'embauche',
      dataIndex: 'hireDate',
      key: 'hireDate',
      width: 140,
      render: (date: string) => new Date(date).toLocaleDateString('fr-FR'),
      sorter: true,
    },
    {
      title: 'Statut',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean) => (
        <Badge
          status={isActive ? 'success' : 'default'}
          text={isActive ? 'Actif' : 'Inactif'}
        />
      ),
      filters: [
        { text: 'Actif', value: true },
        { text: 'Inactif', value: false },
      ],
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => {
        const canEdit = isSuperAdmin() && record.role !== 'administrator';
        const canToggleStatus = canEdit && record.userId !== user?.id;

        const menuItems = [
          {
            key: 'view',
            icon: <EyeOutlined />,
            label: 'Voir les détails',
            onClick: () => openModal('detail', record),
          },
          ...(canEdit ? [{
            key: 'edit',
            icon: <EditOutlined />,
            label: 'Modifier',
            onClick: () => openModal('edit', record),
          }] : []),
          ...(canToggleStatus ? [
            {
              type: 'divider' as const,
            },
            ...(record.isActive ? [{
              key: 'deactivate',
              icon: <StopOutlined />,
              label: 'Désactiver',
              danger: true,
              onClick: () => handleDeactivateStaff(record.id),
            }] : [{
              key: 'activate',
              icon: <PlayCircleOutlined />,
              label: 'Activer',
              onClick: () => handleActivateStaff(record.id),
            }]),
          ] : []),
        ];

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<MoreOutlined />}
              style={{ border: 'none' }}
            />
          </Dropdown>
        );
      },
    },
  ];

  // Statistiques calculées
  const statsCards = useMemo(() => {
    if (!staff || !staff.length) return [];

    const totalStaff = staff.length;
    const activeStaff = staff.filter((s: StaffMember) => s.isActive).length;
    const accountants = staff.filter((s: StaffMember) => s.role === 'accountant').length;
    const creditAgents = staff.filter((s: StaffMember) => s.role === 'credit_agent').length;

    return [
      {
        title: 'Total du personnel',
        value: totalStaff,
        color: '#1890ff',
        icon: <UserOutlined />,
      },
      {
        title: 'Personnel actif',
        value: activeStaff,
        color: '#52c41a',
        icon: <CheckCircleOutlined />,
      },
      {
        title: 'Comptables',
        value: accountants,
        color: '#722ed1',
        icon: <UserOutlined />,
      },
      {
        title: 'Agents de crédit',
        value: creditAgents,
        color: '#13c2c2',
        icon: <UserOutlined />,
      },
    ];
  }, [staff]);

  return (
    <div style={{ padding: '24px', minHeight: '100vh' }}>
      {/* En-tête */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Title level={2} style={{ margin: 0, color: '#1a1a1a' }}>
              Gestion du Personnel 👥
            </Title>
            <Text style={{ color: '#666', fontSize: '16px' }}>
              Gérez les membres de votre équipe et leurs permissions
            </Text>
          </div>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              disabled={isLoading}
            >
              Actualiser
            </Button>
            {isSuperAdmin() && (
              <Button
                icon={<SecurityScanOutlined />}
                onClick={() => navigate('/staff/permissions')}
                style={{
                  background: '#722ed1',
                  borderColor: '#722ed1',
                  color: 'white',
                }}
              >
                Gérer les Permissions
              </Button>
            )}
            {isSuperAdmin() && (
              <>
                <Button
                  type="default"
                  icon={<UserOutlined />}
                  onClick={() => openModal('createCommunalAdmin')}
                  style={{
                    background: '#722ed1',
                    borderColor: '#722ed1',
                    color: 'white',
                  }}
                >
                  Créer Admin Communal
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => openModal('create')}
                  style={{
                    background: '#52c41a',
                    borderColor: '#52c41a',
                  }}
                >
                  Ajouter un personnel
                </Button>
              </>
            )}
          </Space>
        </div>
      </div>

      {/* Cartes de statistiques */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        {statsCards.map((stat, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card
              style={{
                borderRadius: '12px',
                background: '#fff',
                border: '1px solid #f0f0f0',
              }}
            >
              <Statistic
                title={stat.title}
                value={stat.value}
                valueStyle={{ color: stat.color, fontSize: '24px', fontWeight: 'bold' }}
                prefix={stat.icon}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filtres et recherche */}
      <Card style={{ marginBottom: '24px', borderRadius: '12px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} lg={8}>
            <Search
              placeholder="Rechercher par nom, email ou numéro"
              allowClear
              onSearch={handleSearch}
              style={{ width: '100%' }}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col xs={24} sm={6} lg={4}>
            <Select
              placeholder="Rôle"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => handleFilterChange('role', value)}
            >
              {STAFF_ROLE_OPTIONS.map(role => (
                <Option key={role.value} value={role.value}>
                  {role.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={6} lg={4}>
            <Select
              placeholder="Statut"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => handleFilterChange('isActive', value)}
            >
              <Option value={true}>Actif</Option>
              <Option value={false}>Inactif</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} lg={8} style={{ textAlign: 'right' }}>
            <Space>
              <Button 
                icon={<FilterOutlined />}
                onClick={() => openModal('filters')}
              >
                Filtres avancés
              </Button>
              <Button 
                icon={<ExportOutlined />}
                onClick={() => openModal('export')}
                disabled={staff.length === 0}
              >
                Exporter
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Tableau du personnel */}
      <Card style={{ borderRadius: '12px' }}>
        <Table
          columns={columns}
          dataSource={staff}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: searchParams.page,
            pageSize: searchParams.limit,
            total: pagination?.totalItems,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} sur ${total} éléments`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
          size="large"
          rowClassName={(record) =>
            !record.isActive ? 'inactive-row' : ''
          }
        />
      </Card>

      {/* Modals */}
      <StaffCreateModal
        open={modalStates.create}
        onClose={() => closeModal('create')}
        onSubmit={createStaff}
        loading={isCreating}
      />

      <StaffEditModal
        open={modalStates.edit}
        onClose={() => closeModal('edit')}
        onSubmit={(payload) => updateStaff({ 
          id: selectedStaff!.id, 
          payload 
        })}
        loading={isUpdating}
        staff={selectedStaff}
      />

      <StaffDetailModal
        open={modalStates.detail}
        onClose={() => closeModal('detail')}
        staff={selectedStaff}
        onEdit={() => {
          closeModal('detail');
          openModal('edit', selectedStaff!);
        }}
        canEdit={isSuperAdmin()}
      />

      <StaffAdvancedFilters
        open={modalStates.filters}
        onClose={() => closeModal('filters')}
        onApplyFilters={(filters) => setSearchParams(prev => ({ ...prev, ...filters }))}
        initialFilters={searchParams}
      />

      <StaffExportModal
        open={modalStates.export}
        onClose={() => closeModal('export')}
        staff={staff}
      />

      <CommunalAdminCreateModal
        visible={modalStates.createCommunalAdmin}
        onCancel={() => closeModal('createCommunalAdmin')}
        onSuccess={() => {
          closeModal('createCommunalAdmin');
          refetch();
        }}
      />

      <style>{`
        .inactive-row {
          background-color: #fafafa !important;
          opacity: 0.7;
        }
        .inactive-row:hover {
          background-color: #f5f5f5 !important;
        }
      `}</style>
    </div>
  );
};

export default StaffPage;